// frontend/modules/drafts/components/DraftProcessHandler.tsx
import React, { useEffect, useState } from 'react';
import { Card, Typography, Empty, Alert, Spin } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '@/components/common/ThemeProvider';
import { useDrafts } from '../hooks/useDrafts';
import { DraftProcessHandler } from '../types';

const { Title, Text } = Typography;

interface DraftProcessResumeProps {
  handlers: DraftProcessHandler[];
}

const DraftProcessResume: React.FC<DraftProcessResumeProps> = ({ handlers }) => {
  const { dark } = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [handler, setHandler] = useState<DraftProcessHandler | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    activeDraftProcess,
    isLoading,
    setActiveDraftProcess,
    loadDraftProcesses
  } = useDrafts();

  // Load draft process when component mounts
  useEffect(() => {
    const fetchDraft = async () => {
      try {
        if (id) {
          // This would be replaced with a proper API call to get a single draft
          await loadDraftProcesses();
        }
      } catch (err) {
        console.error('Error loading draft process:', err);
        setError('Failed to load draft process. Please try again.');
      }
    };

    fetchDraft();
  }, [id, loadDraftProcesses]);

  // Find the appropriate handler for the draft process
  useEffect(() => {
    if (activeDraftProcess) {
      const matchingHandler = handlers.find(h => 
        h.canHandle(activeDraftProcess.processType)
      );
      
      if (matchingHandler) {
        setHandler(matchingHandler);
        setError(null);
      } else {
        setHandler(null);
        setError(`No handler found for process type: ${activeDraftProcess.processType}`);
      }
    }
  }, [activeDraftProcess, handlers]);

  const handleDraftComplete = () => {
    // Reset active draft and navigate back to drafts list
    setActiveDraftProcess(null);
    navigate('/drafts');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-4">
        <Spin size="large" tip="Loading draft process..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        className="mb-4"
      />
    );
  }

  if (!activeDraftProcess) {
    return (
      <Card className={dark ? 'bg-gray-800 text-white' : ''}>
        <Empty
          description={
            <span className={dark ? 'text-gray-300' : ''}>
              Draft process not found
            </span>
          }
        />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card
        className={dark ? 'bg-gray-800 text-white' : ''}
        title={
          <Title level={4} className={dark ? 'text-white mb-0' : 'mb-0'}>
            Resume: {activeDraftProcess.name}
          </Title>
        }
      >
        <div className="mb-4">
          <Text className={dark ? 'text-gray-300' : 'text-gray-600'}>
            Process Type: {activeDraftProcess.processType}
          </Text>
        </div>

        {handler ? (
          handler.renderResumeComponent(activeDraftProcess, handleDraftComplete)
        ) : (
          <Alert
            message="Handler Not Found"
            description={`No handler available for process type: ${activeDraftProcess.processType}`}
            type="warning"
            showIcon
          />
        )}
      </Card>
    </div>
  );
};

export default DraftProcessResume;
